import React from 'react'
import { useTranslation } from 'react-i18next'
import { surface } from '../ui/surface'

const StatRow = ({ label, value, valueClass = 'text-white' }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="text-sm text-muted-400">{label}</span>
    <span className={`font-display text-lg ${valueClass}`}>{value}</span>
  </div>
)

const RegionsStatsCard = ({ regionStats, onClick, t }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full cursor-pointer rounded-2xl p-5 text-left transition duration-300 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${surface.card} ${surface.hover}`}
  >
    <div className="mb-4 flex items-start justify-between gap-3">
      <h3 className="font-display text-lg text-white">{regionStats.region}</h3>
      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/[0.04] text-muted-300">
        <span className="material-icons text-lg">location_city</span>
      </span>
    </div>

    <div className="space-y-3">
      <StatRow label={t('regionsStats.totalDevices')} value={regionStats.totalDevices} />
      <StatRow label={t('common.online')} value={regionStats.onlineDevices} valueClass="text-positive" />
      <StatRow label={t('common.offline')} value={regionStats.offlineDevices} valueClass="text-critical" />

      <div className="h-1 overflow-hidden rounded-full bg-white/[0.06]">
        <div
          className="h-full rounded-full bg-positive/80 transition-all duration-500"
          style={{ width: `${regionStats.onlinePercentage}%` }}
        />
      </div>

      <div className="flex items-center justify-between">
        <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-500">
          {t('regionsStats.availability')}
        </span>
        <span className="text-sm text-white">{regionStats.onlinePercentage}%</span>
      </div>

      <p className="text-xs text-muted-400">{t('regionsStats.schools', { count: regionStats.schoolsCount })}</p>
    </div>
  </button>
)

const RegionsStatsSkeleton = () => (
  <div className={`min-h-[220px] animate-pulse rounded-2xl p-5 ${surface.card}`}>
    <div className="mb-4 h-5 w-32 rounded-full bg-white/[0.06]" />
    <div className="space-y-3">
      <div className="h-4 w-full rounded-full bg-white/[0.04]" />
      <div className="h-4 w-full rounded-full bg-white/[0.04]" />
      <div className="h-4 w-2/3 rounded-full bg-white/[0.04]" />
    </div>
  </div>
)

const RegionsStats = ({ data, isLoading, onRegionClick }) => {
  const { t } = useTranslation();
  
  if (isLoading) {
    return (
      <section className={`rounded-3xl p-6 ${surface.section}`}>
        <header className="mb-6">
          <h3 className="font-display text-xl text-white md:text-2xl">{t('regionsStats.title')}</h3>
          <p className="mt-1 text-sm text-muted-400">{t('regionsStats.subtitle')}</p>
        </header>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <RegionsStatsSkeleton key={index} />
          ))}
        </div>
      </section>
    )
  }

  if (!data || data.length === 0) {
    return (
      <section className={`rounded-3xl p-6 ${surface.section}`}>
        <header className="mb-6">
          <h3 className="font-display text-xl text-white md:text-2xl">{t('regionsStats.title')}</h3>
          <p className="mt-1 text-sm text-muted-400">{t('regionsStats.subtitle')}</p>
        </header>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <span className="material-icons mb-3 text-4xl text-muted-500">location_off</span>
            <p className="text-sm text-muted-400">{t('common.noData')}</p>
          </div>
        </div>
      </section>
    )
  }

  // Группируем по CITY (районам) для группировки карточек
  const citiesMap = new Map()
  data.forEach(item => {
    const city = item.city || 'Неизвестно'
    const region = item.region || 'Неизвестно'
    
    // Группируем по city (район)
    if (!citiesMap.has(city)) {
      citiesMap.set(city, [])
    }
    citiesMap.get(city).push(item)
  })

  // Сортируем районы
  const groupedData = Array.from(citiesMap.entries()).map(([city, regions]) => {
    return {
      city,
      regions: regions.sort((a, b) => (a.region || '').localeCompare(b.region || '', 'ru'))
    }
  }).sort((a, b) => (a.city || '').localeCompare(b.city || '', 'ru')) // Сортируем районы по алфавиту

  return (
    <section className={`rounded-3xl p-6 ${surface.section}`}>
      <header className="mb-6">
        <h3 className="font-display text-xl text-white md:text-2xl">{t('regionsStats.title')}</h3>
        <p className="mt-1 text-sm text-muted-400">{t('regionsStats.subtitle')}</p>
      </header>

      <div className="space-y-8">
        {groupedData.map((cityGroup, cityIndex) => (
          <div key={`city-${cityGroup.city}-${cityIndex}`}>
            <h4 className="mb-4 font-display text-lg text-white">{cityGroup.city}</h4>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
              {cityGroup.regions.map((regionStats, regionIndex) => (
                <RegionsStatsCard
                  key={`region-${regionStats.region}-${regionStats.city}-${regionIndex}`}
                  regionStats={regionStats}
                  onClick={() => onRegionClick && onRegionClick(regionStats.region)}
                  t={t}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

export default RegionsStats

